import type { Rank } from '@jusbrasil-web/shared-ga4-events'
import { GA4ComponentViewEvent } from '@jusbrasil-web/shared-ga4-events'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { useGA4Dispatcher } from './use-ga4-dispatcher'
import { type GA4Parameters, setPanelParameters } from './utils'

const DEBOUNCE_MS = 500

type ViewedComponentInfo = {
  componentName: string
  componentClass?: string
  componentRank?: Rank
  panelClass?: string
  panelName?: string
  panelRank?: Rank
  resourceId?: string
  viewedAt?: number
  reported?: boolean
}

// Fields that establish a component's identity. viewedAt and reported are excluded
// because they are ephemeral, not identity. The `satisfies` constraint makes TypeScript
// fail here if an identity field is added to or removed from ViewedComponentInfo, forcing
// this list — and therefore the dedup check below — to stay in sync with the type.
const IDENTITY_FIELDS = {
  componentName: true,
  componentClass: true,
  componentRank: true,
  panelClass: true,
  panelName: true,
  panelRank: true,
  resourceId: true,
} satisfies Record<keyof Omit<ViewedComponentInfo, 'viewedAt' | 'reported'>, true>

// Two components are the same when every identity field matches, so components sharing a
// name but differing in class, rank, or resourceId are not treated as duplicates.
function isSameComponent(a: ViewedComponentInfo, b: ViewedComponentInfo): boolean {
  return (Object.keys(IDENTITY_FIELDS) as (keyof typeof IDENTITY_FIELDS)[]).every(
    (field) => a[field] === b[field]
  )
}

interface GA4EnricherContextType {
  top: boolean
  ga4Parameters: GA4Parameters
  addViewedComponent: (component: ViewedComponentInfo) => void
}

const GA4EnricherContext = createContext<GA4EnricherContextType | null>(null)

// Returned when no GA4EnricherProvider is mounted. send() still works (it just enriches
// with empty panel parameters), but addViewedComponent is a no-op — so trackView would
// silently report nothing. useHasGA4Enricher lets callers guard against that.
const DEFAULT_ENRICHER: GA4EnricherContextType = {
  top: true,
  ga4Parameters: {},
  addViewedComponent: () => undefined,
}

type GA4EnricherProviderProps = {
  children: React.ReactNode
  ga4Parameters: GA4Parameters
}

export function GA4EnricherProvider(props: GA4EnricherProviderProps) {
  const { children } = props

  const parentGA4Enricher = useGA4Enricher()
  const isTopEnricher = parentGA4Enricher.top

  const ga4ParametersRef = useRef({
    ...parentGA4Enricher.ga4Parameters,
    ...props.ga4Parameters,
  })

  const viewedComponentsRef = useRef<Set<ViewedComponentInfo>>(new Set())
  const [newComponentCount, setNewComponentCount] = useState(0)

  const addViewedComponent = useCallback((component: ViewedComponentInfo) => {
    if (Array.from(viewedComponentsRef.current).some((c) => isSameComponent(c, component))) return

    viewedComponentsRef.current.add(component)
    setNewComponentCount((n) => n + 1)
  }, [])

  const { sendGA4Event } = useGA4Dispatcher()

  useEffect(() => {
    if (!isTopEnricher) return

    // Debounce: wait 500ms before dispatching to batch multiple components that became
    // visible in quick succession (e.g. scroll, drawer open) into a single component_view.
    const timer = setTimeout(() => {
      const unreportedComponents = Array.from(viewedComponentsRef.current).filter(
        (c) => !c.reported
      )
      if (unreportedComponents.length === 0) return

      const event = new GA4ComponentViewEvent({
        viewedComponents: unreportedComponents.map((c) => ({
          componentName: c.componentName,
          componentClass: c.componentClass ?? null,
          componentRank: c.componentRank ?? null,
          panelName: c.panelName ?? null,
          panelClass: c.panelClass ?? null,
          panelRank: c.panelRank ?? null,
          resourceId: c.resourceId ?? null,
          viewedAt: c.viewedAt ?? null,
        })),
      })

      setPanelParameters(event, ga4ParametersRef.current)
      sendGA4Event(event)

      unreportedComponents.forEach((c) => {
        c.reported = true
      })
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [newComponentCount, isTopEnricher, sendGA4Event])

  const providerValues = isTopEnricher ? { addViewedComponent } : parentGA4Enricher

  return (
    <GA4EnricherContext.Provider
      value={{
        ...providerValues,
        top: false,
        ga4Parameters: ga4ParametersRef.current,
      }}
    >
      {children}
    </GA4EnricherContext.Provider>
  )
}

export function useGA4Enricher() {
  return useContext(GA4EnricherContext) ?? DEFAULT_ENRICHER
}

/** Whether a GA4EnricherProvider is mounted as an ancestor. */
export function useHasGA4Enricher() {
  return useContext(GA4EnricherContext) !== null
}
